import { sec } from './units.ts';

/*
 * The Unsleeping (docs/ANTIHEROES.md): guardians of the old garrison the titan keeps dreaming, twisted, on the
 * nightmares' side. Each fights like a hero — a blow, two skills and an ultimate charged by a rage bar — far
 * stronger than any hero; a warlord's place in a bastion wave is his. Not felled, he flees and comes back.
 */

export type UnsleepingId = 'corvin' | 'vela' | 'ansel' | 'lucan' | 'bruni' | 'ragna' | 'edda' | 'kaela';
/** The cast, in the order a group first meets them (the first is always Corvin). */
export const UNSLEEPING_IDS: readonly UnsleepingId[] = ['corvin', 'vela', 'ansel', 'lucan', 'bruni', 'ragna', 'edda', 'kaela'];

/** Each one's health against the party's (`UNSLEEPING.hpByParty`), how many more come in his guard, and his ultimate's name (its skill event is `<id><ult>`). */
export const UNSLEEPING_OF: Record<UnsleepingId, { hpBp: number; escortMore: number; ult: string }> = {
  corvin: { hpBp: 10000, escortMore: 0, ult: 'Night' },
  kaela: { hpBp: 9000, escortMore: 0, ult: 'Forest' },
  lucan: { hpBp: 9000, escortMore: 0, ult: 'Chapel' },
  edda: { hpBp: 12000, escortMore: 2, ult: 'Pillar' },
  vela: { hpBp: 8000, escortMore: 0, ult: 'Rain' },
  ansel: { hpBp: 8500, escortMore: 0, ult: 'Library' },
  bruni: { hpBp: 10000, escortMore: 0, ult: 'Forge' },
  ragna: { hpBp: 11000, escortMore: 0, ult: 'Sally' },
};

/** When they come, how hard, and how they come back (every Unsleeping alike). */
export const UNSLEEPING = {
  /** First step (0-based) one may come, the step the group's level calls for (WAVE.levelMarea; mean level 4), never the calendar's. */
  fromMarea: 1,
  /** Never met yet: he comes on the first bastion wave he may (the group's first sight of him). */
  firstAtOnce: true,
  /** After that, one bastion wave in this many brings one: in era I, and from era II on. */
  everyEraI: 3,
  everyLater: 2,
  /** At most one fight with the Unsleeping per Marea (whoever it was), however many of the cast are left. */
  onePerMarea: true,
  /** The rest of the wave's points once he takes the warlord's place. 6500 until 2026-11-03: the user wanted him on top of a full wave. */
  waveBudgetBp: 10000,
  /** Party size (heroes in the campaign, 1 to 8) scales his health. */
  hpByParty: [6750, 6750, 9750, 12750, 15000, 17250, 19500, 21750, 24000],
  /** The party's mean level above 1 raises his health and his blows (per level, bp). */
  hpPerLevelBp: 1900,
  dmgPerLevelBp: 1200,
  /** Each time he came back: stronger in health and blows. He flees at most `maxReturns` times; then he stands till he falls. */
  returnBp: 1500,
  maxReturns: 3,
  /** Two at once only when the party's mean level is this high (docs/ANTIHEROES.md, decided 2026-10-29). */
  twoFromLevel: 20,
  /** A hero his skill held (stun, challenge) can't be held by him again for this long. */
  heroControlImmune: sec(6),
  /** He walks in at this share of the wave's spawn window (mid-wave, never as its epilogue). */
  arriveBp: 4500,
  /**
   * The ultimate: the bar fills a little every tick and with every blow he takes (1 % of his life lost → `ragePerPct`).
   * Each new phase fills it at once, so every fight with him sees the Night more than once.
   */
  rageMax: sec(30),
  ragePerPct: sec(1),
  /** The channel before it lands: longer for a party of two or fewer, so it can still be cut. */
  channel: sec(2.5),
  channelSmall: sec(3.5),
  /** Heroes' damage during the channel (bp of his max health) that cuts it: stunned and exposed (towers don't count). */
  breakBp: 1000,
  brokenStun: sec(3),
  exposedTicks: sec(3),
  exposedBp: 15000,
  /** After his ultimate lands, nothing holds him for a moment. */
  unstoppable: sec(3),
  /** Phases: at these shares of his life he grows (and loses a piece of armour). */
  phasesBp: [6600, 3300],
  /** In his last phase he takes this share of every blow (he doesn't melt at the end). */
  lastTakenBp: 7000,
  /** He flees at this share of his life; it takes `fleeTicks`, and the heroes hold him only by dealing `fleeHoldBp` of his life meanwhile. */
  fleeAtBp: 1500,
  fleeHoldBp: 300,
  fleeTicks: sec(2),
  fleeRetry: sec(4),
  /** Felled for good: dust for the storehouse (besides a boss's rare piece and experience). */
  felledDust: 150,
  /** His guard: nightmares of the wave's grade that come in with him, by party size. */
  escort: { kind: 'skeleton' as const, byParty: [2, 2, 2, 3, 3, 4, 4, 4, 4] },
};

/** Ser Corvin, the Knight of the Gate (warrior): he held the gate with his shoulder until it gave. */
export const CORVIN = {
  /** Blows from a hero in front of him (within `frontHalf` of where he faces, 1024 a turn) land at this share. */
  frontTakenBp: 7000,
  frontHalf: 170,
  /**
   * His blows and skills hurt by the hero's own health, through armour and shields (a cleric's ward, a paladin's
   * devotion and sacrifice still help): each blow at least `blowMaxHpBp` of the struck hero's max health, also on
   * every other hero within `cleave` in front of him.
   */
  blowMaxHpBp: 900,
  cleave: { range: 1500, half: 171 },
  /** Challenge: he marks the nearest hero; struck by him while it hits another, the mark's blow lands double. Two marks in phase 2. */
  challenge: { every: sec(7), range: 6000, ticks: sec(5), doubleBp: 20000 },
  /** Gate charge: a line up to `range`, stunning and striking all it crosses; a wall or gate on the line takes `wallBp` and stops it. */
  charge: { every: sec(14), range: 5000, minRange: 1800, step: 520, width: 750, stun: sec(1), damage: 60, maxHpBp: 1500, wallBp: 40000 },
  /**
   * The Night of the Gate (ultimate): a wall of shadow in a cone before him. Heroes take `damage` or `maxHpBp` of their max health (the higher), and are thrown
   * back `knock`; walls and gates in it break, towers take `towerBp` of their health. In his last phase the ground stays dark and hurts.
   */
  night: { radius: 4200, half: 171, damage: 230, maxHpBp: 5000, knock: 1500, towerBp: 5000, shadow: { ticks: sec(6), damage: 14, radius: 1500 } },
};
